import {z} from 'zod';

/**
 * The congestion windows a request is made of, and the quarters a picker may offer for
 * them.
 *
 * **Its own module because `domain/request.ts` needs it.** A stored request carries the
 * windows it was submitted with (ADR 0034), and `request-contract.ts` already imports
 * `dispatchDaySchema` from `request.ts` — so the schema cannot live there without the two
 * files importing each other. Nothing here needs a dispatch day or an id, which is what
 * makes the split clean rather than arbitrary.
 */
/**
 * `HH:MM` on a quarter of the hour, which is the one constraint a DSO's window has (ADR
 * 0037): the solver answers per quarter, and a window between two quarters would ask about
 * part of one.
 */
const quarterTimeSchema = z.string().regex(/^([01]\d|2[0-3]):(00|15|30|45)$/);

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_QUARTER = 15;
/**
 * How far into the dispatch day a time falls, in minutes from midnight.
 *
 * A dispatch day is the Amsterdam calendar day (`docs/answered-questions.md` §30), so a
 * time's position in the day is its own clock reading.
 */
const offsetOf = (time: string): number =>
  // `quarterTimeSchema` has already matched `HH:MM`, so both slices are two digits. Read
  // rather than destructured-and-asserted: a `!` here is a claim the type system cannot
  // check, and the static analysis gate is right to say so.
  Number(time.slice(0, 2)) * MINUTES_PER_HOUR + Number(time.slice(3, 5));

const MINUTES_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR;

/**
 * The same clock for a time used as the *end* of a window, where `00:00` is the close of
 * the day rather than its start. Without this the day's last quarter could not be requested
 * at all: `offsetOf('00:00')` is zero, so no `from` was ever less than it.
 */
const endOffsetOf = (time: string): number => offsetOf(time) || MINUTES_PER_DAY;

/** How long a window lasts, in minutes. Negative when it runs backwards, which is a refusal. */
const durationMinutesOf = (window: {from: string; to: string}): number =>
  endOffsetOf(window.to) - offsetOf(window.from);

/**
 * A stretch of the day the DSO wants relieved, and how much of the available flexibility
 * to ask for.
 *
 * **The amount is a percentage**, because kW is Solvie's *output*, computed per resource
 * group against that group's `ACTIVE_AVAILABLE_FLEX`. A caller asking in kW would be
 * deciding a number they cannot know for a group they cannot see.
 */
/**
 * **Asked for in whole percent; read back as whatever was stored.** The share is a number a
 * DSO decides rather than one they measure, and `33.33%` read as a precision the request
 * does not have — so a window being asked for is an integer. Requests submitted while two
 * decimals were allowed are still in the table, though, and a row the schema refuses is a
 * request nobody can open, list or publish. Tightening an input decides what may be asked
 * for next; it never decides what already happened.
 */
const windowSchemaWith = (flexPercent: z.ZodType<number>) =>
  z
    .object({
      from: quarterTimeSchema,
      to: quarterTimeSchema,
      /** Percentage of the available flexibility. */
      flexPercent,
    })
    .strict()
    /**
     * **No length rule** (ADR 0037). A window says when the grid is under stress; how long a
     * group is held below its baseline is the interval's business, and `dispatchIntervalsOf`
     * cuts that to the dispatch. What stays is that the length is positive: a dispatch day
     * is a calendar day, so a window may not cross midnight, and `22:00`–`02:00` measures
     * minus twenty hours.
     */
    .refine(window => durationMinutesOf(window) > 0, {
      message: 'invalid_value',
      path: ['to'],
    })
    /**
     * Said in words as well, because a cross-field `.refine()` is dropped by
     * `z.toJSONSchema` (`http/openapi.ts`): without this the published document describes
     * `from` and `to` as any two quarters, and a caller reading it would build requests the
     * API refuses.
     *
     * **The day-dependent half cannot be said in JSON Schema at all**: whether `02:15`
     * happens depends on `dispatchDay`, which is a field of the request rather than of the
     * window, and on the Amsterdam tz database. So it is named here too, and
     * `previewRequestFor` is where it is enforced.
     */
    .describe(
      'A window runs from one quarter of an hour to a later one on the dispatch day; 00:00 as `to` ' +
        'is the close of the day. On the day the clocks go forward, a time the day skips cannot be named.',
    );

/** What a caller may ask for: whole percent of the flexibility on offer. */
export const congestionWindowSchema = windowSchemaWith(z.number().int().min(0).max(100));

/** What a stored request may hold, including the fractions that were once accepted. */
export const storedCongestionWindowSchema = windowSchemaWith(z.number().min(0).max(100));

export type CongestionWindow = z.infer<typeof congestionWindowSchema>;

/** A position in the day as `HH:MM`, with the close of the day spelled `00:00`. */
const timeAt = (minutes: number): string => {
  const inDay = minutes % MINUTES_PER_DAY;
  return `${String(Math.floor(inDay / MINUTES_PER_HOUR)).padStart(2, '0')}:${String(inDay % MINUTES_PER_HOUR).padStart(2, '0')}`;
};

/**
 * Every quarter a window may start at, in the order the dispatch day runs them: 00:00 to
 * 23:45. Exported so a picker offers them in that order and cannot put a reader in a
 * position where `from` is later in the day than `to`.
 */
export const WINDOW_START_TIMES = Array.from({length: MINUTES_PER_DAY / MINUTES_PER_QUARTER}, (_unused, index) =>
  timeAt(index * MINUTES_PER_QUARTER),
);

/**
 * The end a window gets when its start moves: the same *length*, not the same end. A
 * reader who asked for four hours from 08:00 and then moved the start to 10:00 still wants
 * four hours. Where the rest of the day has no room for it, the window runs to the close.
 */
export const endKeepingLength = (previous: {from: string; to: string}, from: string): string =>
  timeAt(Math.min(offsetOf(from) + durationMinutesOf(previous), MINUTES_PER_DAY));

/**
 * The ends a window starting at `from` may have, earliest first: every later quarter, and
 * `00:00` last as the *close* of the day — the same time that opens it, which is how a
 * window reaching midnight is expressed at all.
 *
 * Exported so the picker offers exactly the pairs the schema accepts rather than restating
 * the rule next to it, where the two could drift.
 */
export const windowEndTimesFor = (from: string): string[] => [
  ...WINDOW_START_TIMES.filter(time => offsetOf(time) > offsetOf(from)),
  '00:00',
];

const overlaps = (windows: CongestionWindow[]): boolean => {
  const sorted = [...windows].sort((a, b) => offsetOf(a.from) - offsetOf(b.from));
  return sorted.some((window, index) => {
    // `.at()` rather than `sorted[index + 1]`: the last element has no next one, and only
    // `.at()` says so in a way that survives a compile without our `tsconfig`. Codacy runs
    // one — `noUncheckedIndexedAccess` off — where an index read is never `undefined`, so
    // the guard below reads as a condition that cannot fail and trips `no-unnecessary-
    // condition` (ADR 0033).
    const next = sorted.at(index + 1);
    return next !== undefined && offsetOf(next.from) < endOffsetOf(window.to);
  });
};

/**
 * The whole request may ask for at most all of the flexibility on offer, so the
 * percentages add up over the windows rather than each being judged alone: three windows
 * at 80% is 240% of what the groups have, and a curve nobody can deliver is worth
 * refusing at the form rather than at the VEN.
 */
const MAX_TOTAL_PERCENT = 100;

const totalPercent = (windows: CongestionWindow[]): number =>
  windows.reduce((total, window) => total + window.flexPercent, 0);

/**
 * The same two rules for a set of windows, whichever percentages the single window takes:
 * they may not overlap, and together they may not ask for more than there is.
 */
const windowsSchemaOf = (window: z.ZodType<CongestionWindow>) =>
  z
    .array(window)
    .min(1)
    .refine(windows => !overlaps(windows), {message: 'invalid_value'})
    .refine(windows => totalPercent(windows) <= MAX_TOTAL_PERCENT, {message: 'too_big'});

/** What a caller may ask for. */
export const congestionWindowsSchema = windowsSchemaOf(congestionWindowSchema);

/** What a stored request may hold. */
export const storedCongestionWindowsSchema = windowsSchemaOf(storedCongestionWindowSchema);
